import { z } from 'zod';

import {
  PolicySettingsReturnsRefundType,
  PolicySettingsWarrantyKind,
  type PatchAdminV1OrgStoresIdBody,
  type PolicySettings,
  type Store,
} from '@nucleus-commerce/api-client/admin';
import { isMoney, money, type Money } from '@nucleus-commerce/core';

export const EXCLUSIONS_MAX = 20;
export const EXCLUSION_LENGTH_MAX = 120;

const wholeIn = (min: number, max: number, message: string) =>
  z
    .string()
    .trim()
    .refine((v) => /^\d+$/.test(v) && Number(v) >= min && Number(v) <= max, { error: message });

const exclusions = z
  .array(z.string().trim().min(1).max(EXCLUSION_LENGTH_MAX))
  .max(EXCLUSIONS_MAX, { error: 'policies:validation.exclusionsMax' });

const moneySchema = z.custom<Money>(isMoney, { error: 'policies:validation.amountRequired' });

/**
 * `settings.policies` as the form edits it. Each nullable backend field becomes a mode plus a
 * value so "no free shipping", "always free" and "free over an amount" are three explicit choices
 * instead of null, 0 and a number.
 */
export const policySettingsSchema = z.object({
  shipping: z
    .object({
      freeShipping: z.enum(['none', 'always', 'over']),
      freeOver: moneySchema.nullable(),
      processingDays: z.boolean(),
      processingMin: z.string().trim(),
      processingMax: z.string().trim(),
      regionsNote: z.string().trim().max(300, { error: 'policies:validation.regionsNoteLength' }),
    })
    .superRefine((shipping, ctx) => {
      if (
        shipping.freeShipping === 'over' &&
        (!shipping.freeOver || shipping.freeOver.amount <= 0)
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['freeOver'],
          message: 'policies:validation.freeOverRequired',
        });
      }
      if (shipping.processingDays) {
        const whole = (v: string) => /^\d+$/.test(v) && Number(v) <= 365;
        if (!whole(shipping.processingMin)) {
          ctx.addIssue({
            code: 'custom',
            path: ['processingMin'],
            message: 'policies:validation.processingDays',
          });
        }
        if (!whole(shipping.processingMax)) {
          ctx.addIssue({
            code: 'custom',
            path: ['processingMax'],
            message: 'policies:validation.processingDays',
          });
        } else if (
          whole(shipping.processingMin) &&
          Number(shipping.processingMax) < Number(shipping.processingMin)
        ) {
          ctx.addIssue({
            code: 'custom',
            path: ['processingMax'],
            message: 'policies:validation.processingOrder',
          });
        }
      }
    }),
  returns: z.object({
    exclusions,
    refundType: z.enum(PolicySettingsReturnsRefundType),
  }),
  warranty: z
    .object({
      kind: z.enum(PolicySettingsWarrantyKind),
      years: z.string().trim(),
      exclusions,
    })
    .superRefine((warranty, ctx) => {
      if (warranty.kind === 'limited' && warranty.years !== '') {
        const ok = wholeIn(1, 100, '').safeParse(warranty.years).success;
        if (!ok) {
          ctx.addIssue({
            code: 'custom',
            path: ['years'],
            message: 'policies:validation.warrantyYears',
          });
        }
      }
    }),
  resizing: z
    .object({
      freeResizing: z.enum(['none', 'always', 'within']),
      months: z.string().trim(),
      exclusions,
    })
    .superRefine((resizing, ctx) => {
      if (resizing.freeResizing === 'within') {
        const ok = wholeIn(1, 120, '').safeParse(resizing.months).success;
        if (!ok) {
          ctx.addIssue({
            code: 'custom',
            path: ['months'],
            message: 'policies:validation.resizingMonths',
          });
        }
      }
    }),
});

export type PolicySettingsForm = z.infer<typeof policySettingsSchema>;

export function fromPolicySettings(
  store: Pick<Store, 'settings' | 'defaultCurrency'>,
): PolicySettingsForm {
  const { shipping, returns, warranty, resizing } = store.settings.policies;
  const freeOver = shipping.freeOverCents;
  return {
    shipping: {
      freeShipping: freeOver === null ? 'none' : freeOver === 0 ? 'always' : 'over',
      freeOver: freeOver ? money(freeOver, store.defaultCurrency) : null,
      processingDays: shipping.processingDays !== null,
      processingMin: shipping.processingDays ? String(shipping.processingDays.min) : '',
      processingMax: shipping.processingDays ? String(shipping.processingDays.max) : '',
      regionsNote: shipping.regionsNote ?? '',
    },
    returns: { exclusions: [...returns.exclusions], refundType: returns.refundType },
    warranty: {
      kind: warranty.kind,
      years: warranty.years === null ? '' : String(warranty.years),
      exclusions: [...warranty.exclusions],
    },
    resizing: {
      freeResizing:
        resizing.freeWithinMonths === null
          ? 'none'
          : resizing.freeWithinMonths === 0
            ? 'always'
            : 'within',
      months: resizing.freeWithinMonths ? String(resizing.freeWithinMonths) : '',
      exclusions: [...resizing.exclusions],
    },
  };
}

/** The whole group as the backend stores it; also what the highlights preview reads. */
export function toPolicySettings(values: PolicySettingsForm): PolicySettings {
  const { shipping, returns, warranty, resizing } = values;
  return {
    shipping: {
      freeOverCents:
        shipping.freeShipping === 'none'
          ? null
          : shipping.freeShipping === 'always'
            ? 0
            : (shipping.freeOver?.amount ?? null),
      processingDays: shipping.processingDays
        ? { min: Number(shipping.processingMin), max: Number(shipping.processingMax) }
        : null,
      regionsNote: shipping.regionsNote || null,
    },
    returns: { exclusions: returns.exclusions, refundType: returns.refundType },
    warranty: {
      kind: warranty.kind,
      years: warranty.kind === 'limited' && warranty.years !== '' ? Number(warranty.years) : null,
      exclusions: warranty.exclusions,
    },
    resizing: {
      freeWithinMonths:
        resizing.freeResizing === 'none'
          ? null
          : resizing.freeResizing === 'always'
            ? 0
            : Number(resizing.months),
      exclusions: resizing.exclusions,
    },
  };
}

export function toPolicySettingsPatch(values: PolicySettingsForm): PatchAdminV1OrgStoresIdBody {
  return { settings: { policies: toPolicySettings(values) } };
}

/**
 * `settings.policies.shipping.freeOverCents` from the backend lands on the form's
 * `shipping.freeOver`; list items (`returns.exclusions.3`) land on the list.
 */
export function policySettingsField(apiField: string): string {
  const path = apiField.replace(/^settings\.policies\./, '');
  return path
    .replace(/^shipping\.freeOverCents$/, 'shipping.freeOver')
    .replace(/^shipping\.processingDays\.min$/, 'shipping.processingMin')
    .replace(/^shipping\.processingDays\.max$/, 'shipping.processingMax')
    .replace(/^shipping\.processingDays$/, 'shipping.processingDays')
    .replace(/^resizing\.freeWithinMonths$/, 'resizing.months')
    .replace(/^(returns|warranty|resizing)\.exclusions\.\d+$/, '$1.exclusions');
}

export const POLICY_SETTINGS_FIELDS: ReadonlySet<string> = new Set([
  'shipping.freeOver',
  'shipping.processingDays',
  'shipping.processingMin',
  'shipping.processingMax',
  'shipping.regionsNote',
  'returns.exclusions',
  'returns.refundType',
  'warranty.kind',
  'warranty.years',
  'warranty.exclusions',
  'resizing.months',
  'resizing.exclusions',
]);
